import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

export default function Review() {
  const { id } = useParams(); // itemId or vendorId
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type'); // 'item' or 'vendor'
  const navigate = useNavigate();
  const { getToken } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [item, setItem] = useState(null);
  const [vendor, setVendor] = useState(null);
  
  // Form state
  const [itemStars, setItemStars] = useState(0);
  const [itemComment, setItemComment] = useState('');
  const [vendorStars, setVendorStars] = useState(0);
  const [vendorComment, setVendorComment] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (type === 'item') {
          const res = await api(`/api/items/${id}`, {});
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Error ${res.status}`);
          }
          const data = await res.json();
          if (!cancelled) {
            setItem(data);
            if (data.vendorId) {
              // Also fetch vendor info for optional vendor review
              const vendorRes = await api(`/api/vendors/${data.vendorId}`, {});
              if (vendorRes.ok) {
                const vendorData = await vendorRes.json();
                if (!cancelled) {
                  setVendor(vendorData);
                }
              }
            }
          }
        } else if (type === 'vendor') {
          const res = await api(`/api/vendors/${id}`, {});
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Error ${res.status}`);
          }
          const data = await res.json();
          if (!cancelled) {
            setVendor(data);
          }
        } else {
          throw new Error('Invalid review type. Must be "item" or "vendor".');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (type === 'item' && itemStars === 0) {
      setError('Please select a star rating for the item.');
      return;
    }
    
    if (type === 'vendor' && vendorStars === 0) {
      setError('Please select a star rating for the vendor.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const promises = [];

      // Submit item review if applicable
      if (type === 'item' && itemStars > 0) {
        promises.push(
          api('/api/reviews/item', {
            getToken,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              itemId: parseInt(id),
              stars: itemStars,
              comment: itemComment.trim() || null,
            }),
          })
        );
      }

      // Submit vendor review if applicable
      if (vendor && vendorStars > 0) {
        const vendorId = type === 'vendor' ? parseInt(id) : item?.vendorId;
        if (vendorId) {
          promises.push(
            api('/api/reviews/vendor', {
              getToken,
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                vendorId: vendorId,
                stars: vendorStars,
                comment: vendorComment.trim() || null,
              }),
            })
          );
        }
      }

      if (promises.length === 0) {
        setError('Please provide at least one review.');
        setSubmitting(false);
        return;
      }

      await Promise.all(promises);

      // Navigate back to the item or vendor page
      if (type === 'item') {
        navigate(`/item/${id}`);
      } else {
        navigate(`/vendor/${id}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (currentStars, setStars) => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setStars(star)}
            className={`text-4xl transition-colors ${
              star <= currentStars
                ? 'text-yellow-400'
                : 'text-slate-300 dark:text-slate-600'
            }`}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <SignedIn>
        <div className="flex items-center justify-center py-16">
          <p className="text-2xl text-slate-500 dark:text-slate-400">Loading…</p>
        </div>
      </SignedIn>
    );
  }

  if (error && !item && !vendor) {
    return (
      <SignedIn>
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-semibold">Write a Review</h1>
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        </div>
      </SignedIn>
    );
  }

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold">Write a Review</h1>

        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Item Review Section */}
          {type === 'item' && item && (
            <div className="flex flex-col gap-4 p-6 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
              <h2 className="text-2xl font-semibold">Review Item</h2>
              <div className="flex flex-col gap-2">
                <p className="text-lg font-medium">{item.title}</p>
                {item.vendorName && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Sold by: {item.vendorName}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <label className="text-lg font-medium">Rating (required)</label>
                {renderStars(itemStars, setItemStars)}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="item-comment" className="text-lg font-medium">
                  Comment (optional)
                </label>
                <textarea
                  id="item-comment"
                  value={itemComment}
                  onChange={(e) => setItemComment(e.target.value)}
                  rows={4}
                  className="h-32 text-lg w-full rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-4 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400"
                  placeholder="Share your thoughts about this item..."
                />
              </div>
            </div>
          )}

          {/* Vendor Review Section */}
          {vendor && (
            <div className="flex flex-col gap-4 p-6 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
              <h2 className="text-2xl font-semibold">
                {type === 'vendor' ? 'Review Vendor' : 'Review Vendor (optional)'}
              </h2>
              <div className="flex flex-col gap-2">
                <p className="text-lg font-medium">
                  {vendor.storeName || 'Unnamed Store'}
                </p>
                {vendor.marketName && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Market: {vendor.marketName}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <label className="text-lg font-medium">
                  Rating {type === 'vendor' ? '(required)' : '(optional)'}
                </label>
                {renderStars(vendorStars, setVendorStars)}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="vendor-comment" className="text-lg font-medium">
                  Comment (optional)
                </label>
                <textarea
                  id="vendor-comment"
                  value={vendorComment}
                  onChange={(e) => setVendorComment(e.target.value)}
                  rows={4}
                  className="h-32 text-lg w-full rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-4 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400"
                  placeholder="Share your thoughts about this vendor..."
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => {
                if (type === 'item') {
                  navigate(`/item/${id}`);
                } else {
                  navigate(`/vendor/${id}`);
                }
              }}
              disabled={submitting}
              className="flex-1 h-16 text-xl rounded-xl font-semibold bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (type === 'item' && itemStars === 0) || (type === 'vendor' && vendorStars === 0)}
              className="flex-1 h-16 text-xl rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Submitting…' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </SignedIn>
  );
}
